/**
 * Page Transitions — smooth crossfade between pages.
 *
 * Uses native View Transitions API (Chrome/Edge 111+).
 * Fallback: manual opacity fade for Safari/Firefox.
 */
(function () {
  'use strict';

  const TRANSITION_DURATION = 300;
  let isTransitioning = false;

  document.addEventListener('click', onLinkClick);
  window.addEventListener('popstate', onPopState);

  function onLinkClick(event) {
    const link = event.target.closest('a[href]');
    if (!link) return;

    // Skip: modifier keys, external, new tab, anchors, excluded
    if (
      event.metaKey || event.ctrlKey || event.shiftKey ||
      link.target === '_blank' ||
      link.hostname !== location.hostname ||
      (link.pathname === location.pathname && link.hash) ||
      link.hasAttribute('data-no-transition') ||
      link.closest('[data-no-transition]')
    ) return;

    // Skip: cart links (handled by cart-drawer), checkout, accounts
    const path = link.pathname;
    if (
      path === '/cart' ||
      path.startsWith('/cart/') ||
      path.includes('/checkout') ||
      path.includes('/account')
    ) return;

    // Skip: cart icon
    if (link.closest('.header__cart-icon')) return;

    // Skip: file resources
    const ext = path.split('.').pop();
    if (['pdf', 'jpg', 'png', 'gif', 'svg', 'zip', 'js', 'css'].includes(ext)) return;

    event.preventDefault();
    if (isTransitioning) return;

    navigateTo(link.href, true);
  }

  function onPopState() {
    if (isTransitioning) return;
    navigateTo(location.href, false);
  }

  async function navigateTo(url, pushState) {
    isTransitioning = true;

    try {
      const fetchPromise = fetchPage(url);

      if (document.startViewTransition) {
        const html = await fetchPromise;
        if (!html) { fallbackNavigate(url); return; }

        const transition = document.startViewTransition(() => {
          applyNewContent(html, url, pushState);
        });

        await transition.finished;
      } else {
        const main = document.querySelector('#MainContent');
        if (!main) { fallbackNavigate(url); return; }

        main.classList.add('page-leaving');
        await wait(TRANSITION_DURATION);

        const html = await fetchPromise;
        if (!html) { fallbackNavigate(url); return; }

        applyNewContent(html, url, pushState);

        main.classList.remove('page-leaving');
        main.classList.add('page-entering');

        await wait(TRANSITION_DURATION);
        main.classList.remove('page-entering');
      }
    } catch (err) {
      console.error('Page transition error:', err);
      fallbackNavigate(url);
    } finally {
      isTransitioning = false;
    }
  }

  async function fetchPage(url) {
    try {
      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) return null;
      return res.text();
    } catch {
      return null;
    }
  }

  function applyNewContent(html, url, pushState) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const currentMain = document.querySelector('#MainContent');
    const newMain = doc.querySelector('#MainContent');
    if (currentMain && newMain) {
      currentMain.innerHTML = newMain.innerHTML;
      for (const attr of newMain.attributes) {
        currentMain.setAttribute(attr.name, attr.value);
      }
    }

    const newTitle = doc.querySelector('title');
    if (newTitle) document.title = newTitle.textContent;

    const currentCanonical = document.querySelector('link[rel="canonical"]');
    const newCanonical = doc.querySelector('link[rel="canonical"]');
    if (currentCanonical && newCanonical) {
      currentCanonical.href = newCanonical.href;
    }

    if (pushState) {
      window.history.pushState({}, '', url);
    }

    window.scrollTo({ top: 0, behavior: 'instant' });
    reinitialize();
  }

  function reinitialize() {
    if (typeof window.initCvSliders === 'function') {
      window.initCvSliders();
    }

    document.dispatchEvent(new CustomEvent('page:transition'));

    const main = document.querySelector('#MainContent');
    if (!main) return;

    main.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function fallbackNavigate(url) {
    isTransitioning = false;
    window.location.href = url;
  }
})();